/**
 * Shared hero background: the static blue LCP image plus the pre-baked WebP
 * blob/noise layers and their animations. Extracted from Hero so the homepage
 * hero and the per-page heroes (PageHero) render an identical backdrop.
 *
 * All layers are absolutely positioned (inset-0) and must sit inside a
 * `relative overflow-hidden` parent. The blob class names are targeted by the
 * keyframe + low-motion + Firefox rules in globals.css, so they must stay.
 */
export function HeroBackground() {
  return (
    <>
      {/* Static background — critical for LCP, load with high priority */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <picture>
          <source srcSet="/background-images/hero-bg-static.avif" type="image/avif" media="(min-width: 1024px)" />
          <source srcSet="/background-images/hero-bg-static.webp" type="image/webp" media="(min-width: 1024px)" />
          <source srcSet="/background-images/hero-bg-static-1280.avif" type="image/avif" media="(min-width: 640px)" />
          <source srcSet="/background-images/hero-bg-static-1280.webp" type="image/webp" media="(min-width: 640px)" />
          <source srcSet="/background-images/hero-bg-static-768.avif" type="image/avif" />
          <img
            src="/background-images/hero-bg-static-768.webp"
            alt=""
            aria-hidden="true"
            fetchPriority="high"
            loading="eager"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover pointer-events-none select-none"
          />
        </picture>
      </div>

      {/* Blob overlays: pre-baked WebP layers (alpha + blur baked in) */}
      {/* Side blob — visible on all screen sizes */}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src="/background-images/blob-left-side-1280.webp"
        srcSet="/background-images/blob-left-side-768.webp 768w, /background-images/blob-left-side-1280.webp 1280w, /background-images/blob-left-side.webp 1920w"
        sizes="100vw"
        alt=""
        aria-hidden="true"
        fetchPriority="low"
        className="blob-left-side absolute inset-0 w-full h-full object-cover pointer-events-none select-none"
      />
      {/* Bottom rect — desktop-only. <source media> prevents download on mobile. */}
      <picture>
        <source media="(min-width: 1024px)" srcSet="/background-images/blob-left-bottom-1280.webp 1280w, /background-images/blob-left-bottom.webp 1920w" sizes="100vw" type="image/webp" />
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
          alt=""
          aria-hidden="true"
          fetchPriority="low"
          className="blob-left-bottom absolute inset-0 w-full h-full object-cover pointer-events-none select-none"
        />
      </picture>
      {/* Diagonal cross-a — desktop-only */}
      <picture>
        <source media="(min-width: 1024px)" srcSet="/background-images/blob-left-cross-a-1280.webp 1280w, /background-images/blob-left-cross-a.webp 1920w" sizes="100vw" type="image/webp" />
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
          alt=""
          aria-hidden="true"
          fetchPriority="low"
          className="blob-left-cross-a absolute inset-0 w-full h-full object-cover pointer-events-none select-none"
        />
      </picture>
      {/* Diagonal cross-b — desktop-only */}
      <picture>
        <source media="(min-width: 1024px)" srcSet="/background-images/blob-left-cross-b-1280.webp 1280w, /background-images/blob-left-cross-b.webp 1920w" sizes="100vw" type="image/webp" />
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
          alt=""
          aria-hidden="true"
          fetchPriority="low"
          className="blob-left-cross-b absolute inset-0 w-full h-full object-cover pointer-events-none select-none"
        />
      </picture>
      {/* Right blob 1 — visible on all (object-position:75% center on mobile via CSS) */}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src="/background-images/blob-right-1-1280.webp"
        srcSet="/background-images/blob-right-1-768.webp 768w, /background-images/blob-right-1-1280.webp 1280w, /background-images/blob-right-1.webp 1920w"
        sizes="100vw"
        alt=""
        aria-hidden="true"
        fetchPriority="low"
        className="hero-bg-g2 absolute inset-0 w-full h-full object-cover pointer-events-none select-none"
      />
      {/* Right blob 2 — desktop-only */}
      <picture>
        <source media="(min-width: 1024px)" srcSet="/background-images/blob-right-2-1280.webp 1280w, /background-images/blob-right-2.webp 1920w" sizes="100vw" type="image/webp" />
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
          alt=""
          aria-hidden="true"
          fetchPriority="low"
          className="hero-bg-g3 absolute inset-0 w-full h-full object-cover pointer-events-none select-none"
        />
      </picture>

      {/* Noise + texture tile — repeated WebP, no blend mode */}
      <div
        className="absolute inset-0 pointer-events-none select-none opacity-[0.7]"
        style={{ backgroundImage: "url('/background-images/hero-noise-and-texture-tile.webp')", backgroundRepeat: 'repeat' }}
        aria-hidden="true"
      />
    </>
  );
}
